/**
 * @fileoverview ChatMessageList - Smooth-scrolling chat message container
 *
 * Built on the shadcn MessageScroller: follows streamed output while the
 * reader is at the live edge (released the moment they scroll away), anchors
 * each new user turn near the top of the viewport with a peek of the
 * previous turn, and shows a floating scroll-to-bottom button when the
 * reader is above the live edge. Premium empty state.
 * Tool activity renders as pill badges that persist through the response.
 */

import { useEffect, useRef, useState } from "react";
import { cn } from "@/utils/misc";
import { Logo } from "@/ui/layout/logo";
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from "@/ui/primitives/message-scroller";
import {
  ChatMessage,
  type ChatMessageData,
  type ChatTaggedDocument,
  type ChatUserInfo,
} from "./ChatMessage";
import type { CitedSource } from "@/components/agents/universal/sourceCitations.helpers";
import type { ChartSpec } from "@cvx/lib/charts/spec";
import type { DiagramSpec } from "@cvx/lib/diagrams/spec";
import type { SpreadsheetSpec } from "@cvx/lib/spreadsheets/spec";
import {
  Loader2,
  Search,
  FileText,
  BookOpen,
  Database,
  Check,
  Briefcase,
  Sparkles,
  CalendarDays,
  BarChart3,
  Table2,
  Workflow,
} from "lucide-react";

/** Friendly display info for each chat tool */
const TOOL_DISPLAY: Record<string, { label: string; icon: typeof Search }> = {
  searchDocuments: { label: "Searched documents", icon: Search },
  getDocumentContent: { label: "Read document", icon: FileText },
  getDocumentMetadata: { label: "Checked document details", icon: BookOpen },
  getSummaryOutput: { label: "Reviewed summary", icon: Database },
  // Universal agent tools
  getJobOverview: { label: "Loaded job overview", icon: Database },
  searchAcrossJob: { label: "Searched this job", icon: Search },
  semanticSearchDocuments: { label: "Searched documents", icon: Search },
  getEntity: { label: "Loaded record", icon: BookOpen },
  listMyJobs: { label: "Listed accessible jobs", icon: Briefcase },
  searchAcrossWorkspace: { label: "Searched workspace", icon: Search },
  searchInJob: { label: "Searched a job", icon: Search },
  semanticSearchAllJobs: {
    label: "Searched documents workspace-wide",
    icon: Search,
  },
  proposeAssetPatch: { label: "Drafted asset change", icon: Sparkles },
  proposeContactPatch: { label: "Drafted contact change", icon: Sparkles },
  getMyCanvas: { label: "Read canvas", icon: BookOpen },
  proposeCanvasPatch: { label: "Drafted canvas update", icon: Sparkles },
  getJobCalendar: { label: "Checked the calendar", icon: CalendarDays },
  getWorkspaceCalendar: { label: "Checked the calendar", icon: CalendarDays },
  createChart: { label: "Created a chart", icon: BarChart3 },
  createDiagram: { label: "Drew a diagram", icon: Workflow },
  createSpreadsheet: { label: "Built a spreadsheet", icon: Table2 },
  listMailThreads: { label: "Checked the mailbox", icon: Search },
  readMailThread: { label: "Read an email thread", icon: BookOpen },
  getMailSentiment: { label: "Checked contact sentiment", icon: Sparkles },
  instructMailAgent: { label: "Briefed the mail agent", icon: Sparkles },
  // Summary-chat propose tool — staged change awaits Apply/Reject above the input.
  proposeEntityPatch: { label: "Drafted change for review", icon: Sparkles },
};

interface ChatMessageListProps {
  messages: ChatMessageData[];
  userInfo?: ChatUserInfo;
  /** Known documents so user-message tags render as clickable chips. */
  taggedDocuments?: ChatTaggedDocument[];
  onDocumentClick?: (documentId: string) => void;
  onSourceClick?: (source: CitedSource) => boolean | undefined;
  /** Save an inline chart into the current thread's canvas. */
  onSaveChart?: (spec: ChartSpec) => void | Promise<void>;
  /** Save an inline Mermaid diagram into the current thread's canvas. */
  onSaveDiagram?: (spec: DiagramSpec) => void | Promise<void>;
  /** Save an inline spreadsheet into the current thread's canvas. */
  onSaveSpreadsheet?: (spec: SpreadsheetSpec) => void | Promise<void>;
  isLoading?: boolean;
  /** Show a thinking indicator below the messages */
  isThinking?: boolean;
  /** Tool names used in the current turn */
  activeToolCalls?: string[];
  /**
   * Live one-line status for long-running work (e.g. "Drafting canvas
   * update… 3.1k characters"). Rendered as an active pill even while a
   * message is streaming, so slow tool-argument generation never looks
   * like a stall.
   */
  activityStatus?: string | null;
  emptyMessage?: string;
  /**
   * Optional clickable suggestion chips shown only when the message
   * list is empty. Mirrors the ChatGPT empty-state quick prompts —
   * clicking one calls `onSuggestionClick(prompt)` which the parent
   * typically forwards into its `onSend` handler.
   */
  suggestions?: Array<{ label: string; prompt: string; icon?: typeof Search }>;
  onSuggestionClick?: (prompt: string) => void;
  /** Custom headline for the empty state (defaults to "How can I help?"). */
  emptyHeadline?: string;
  className?: string;
}

export function ChatMessageList({
  messages,
  userInfo,
  taggedDocuments,
  onDocumentClick,
  onSourceClick,
  onSaveChart,
  onSaveDiagram,
  onSaveSpreadsheet,
  isLoading,
  isThinking,
  activeToolCalls = [],
  activityStatus,
  emptyMessage = "Ask questions about this summary.\nYour Morgan chat has access to the summary and the source documents.",
  emptyHeadline = "How can I help?",
  suggestions,
  onSuggestionClick,
  className,
}: ChatMessageListProps) {
  // Persist tool calls so they don't vanish immediately.
  // Shows them throughout the tool-use + streaming phase,
  // then fades out 3s after the response finishes.
  const [displayedTools, setDisplayedTools] = useState<string[]>([]);
  const [toolsFading, setToolsFading] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // New tools arriving — show them, cancel any pending fade
    if (activeToolCalls.length > 0) {
      setDisplayedTools(activeToolCalls);
      setToolsFading(false);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
      return;
    }
    // Tools gone + not thinking → fade out quickly
    if (!isThinking && displayedTools.length > 0 && !fadeTimerRef.current) {
      fadeTimerRef.current = setTimeout(() => {
        setToolsFading(true);
        // Remove from DOM after fade animation
        setTimeout(() => {
          setDisplayedTools([]);
          setToolsFading(false);
          fadeTimerRef.current = null;
        }, 400);
      }, 600);
    }
  }, [activeToolCalls, isThinking, displayedTools.length]);

  // Clear tools on new user message
  const prevMsgCountRef = useRef(messages.length);
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (messages.length > prevMsgCountRef.current && lastMsg?.role === "user") {
      setDisplayedTools([]);
      setToolsFading(false);
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    }
    prevMsgCountRef.current = messages.length;
  }, [messages]);

  if (isLoading && messages.length === 0) {
    return (
      <div className={cn("flex flex-1 items-center justify-center", className)}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <p className="text-xs text-muted-foreground">
            Loading conversation...
          </p>
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div
        className={cn(
          "flex flex-1 flex-col items-center justify-center gap-5 overflow-y-auto px-6 py-8",
          className,
        )}
      >
        <div className="max-w-[420px] text-center">
          <p className="font-[Lora,serif] text-[27px] font-semibold text-foreground">
            {emptyHeadline}
          </p>
          <p className="mt-2.5 whitespace-pre-line text-sm text-muted-foreground">
            {emptyMessage}
          </p>
        </div>
        {suggestions && suggestions.length > 0 && onSuggestionClick && (
          <div className="grid w-full max-w-[520px] grid-cols-1 gap-2 sm:grid-cols-2">
            {suggestions.map((s) => {
              const Icon = s.icon;
              return (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => onSuggestionClick(s.prompt)}
                  className="group flex items-start gap-2.5 rounded-lg border border-border bg-background px-3 py-2.5 text-left text-xs shadow-sm transition-all hover:border-primary/30 hover:bg-accent/20 hover:shadow"
                >
                  {Icon && (
                    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-primary" />
                  )}
                  <span className="text-foreground/80 group-hover:text-foreground">
                    {s.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const showToolSteps = displayedTools.length > 0;
  const showThinkingDots =
    isThinking && displayedTools.length === 0 && !activityStatus;

  return (
    <MessageScrollerProvider
      autoScroll
      defaultScrollPosition="last-anchor"
      scrollPreviousItemPeek={64}
    >
      <MessageScroller className={cn("flex-1", className)}>
        <MessageScrollerViewport aria-label="Conversation">
          <MessageScrollerContent className="gap-0 py-4">
            {messages.map((msg) => (
              <MessageScrollerItem
                key={msg.id}
                messageId={msg.id}
                scrollAnchor={msg.role === "user"}
              >
                <ChatMessage
                  message={msg}
                  userInfo={userInfo}
                  taggedDocuments={taggedDocuments}
                  onDocumentClick={onDocumentClick}
                  onSourceClick={onSourceClick}
                  onSaveChart={onSaveChart}
                  onSaveDiagram={onSaveDiagram}
                  onSaveSpreadsheet={onSaveSpreadsheet}
                />
              </MessageScrollerItem>
            ))}

            {/* Thinking pill — matches the tool pill style */}
            {showThinkingDots && (
              <MessageScrollerItem messageId="__thinking">
                <div className="flex gap-3 px-5 py-2 duration-300 animate-in fade-in-0">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-background">
                    <Logo
                      compact
                      width={20}
                      height={20}
                      className="text-primary"
                    />
                  </div>
                  <div className="flex min-w-0 flex-wrap gap-1.5 pt-1">
                    <span className="inline-flex items-center gap-1.5 rounded-md border border-accent/40 bg-accent/10 px-2.5 py-1 text-xs font-medium text-foreground/70">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Thinking
                    </span>
                  </div>
                </div>
              </MessageScrollerItem>
            )}

            {/* Live long-running work status (e.g. drafting a canvas update) —
                shown even while the assistant message is streaming, so slow
                tool-argument generation never reads as a stall. */}
            {activityStatus && (
              <MessageScrollerItem messageId="__activity">
                <div className="flex gap-3 px-5 py-2 duration-300 animate-in fade-in-0">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-background">
                    <Logo
                      compact
                      width={20}
                      height={20}
                      className="text-primary"
                    />
                  </div>
                  <div className="flex min-w-0 flex-wrap gap-1.5 pt-1">
                    <span className="inline-flex items-center gap-1.5 rounded-md border border-accent/40 bg-accent/10 px-2.5 py-1 text-xs font-medium text-foreground/70">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {activityStatus}
                    </span>
                  </div>
                </div>
              </MessageScrollerItem>
            )}

            {/* Tool activity pills — persist through response, fade out after */}
            {showToolSteps && (
              <MessageScrollerItem messageId="__tools">
                <div
                  className={cn(
                    "flex gap-3 px-5 py-2 transition-opacity duration-500",
                    toolsFading ? "opacity-0" : "opacity-100",
                  )}
                >
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-background">
                    <Logo
                      compact
                      width={20}
                      height={20}
                      className="text-primary"
                    />
                  </div>
                  <div className="flex min-w-0 flex-wrap gap-1.5 pt-1">
                    {displayedTools.map((toolName, i) => {
                      const display = TOOL_DISPLAY[toolName] ?? {
                        label: "Working",
                        icon: Loader2,
                      };
                      const isActive =
                        isThinking && i === displayedTools.length - 1;
                      return (
                        <span
                          key={`${toolName}-${i}`}
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium duration-300 animate-in fade-in-0 slide-in-from-left-2",
                            isActive
                              ? "border-accent/40 bg-accent/10 text-foreground/70"
                              : "border-emerald-700/20 bg-emerald-700/5 text-emerald-800 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-400",
                          )}
                        >
                          {isActive ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Check className="h-3 w-3" />
                          )}
                          {display.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </MessageScrollerItem>
            )}
          </MessageScrollerContent>
        </MessageScrollerViewport>
        <MessageScrollerButton />
      </MessageScroller>
    </MessageScrollerProvider>
  );
}
